import { Bell, X } from 'lucide-react'

import { NoticeMarkdown } from '@/components/notices/notice-markdown'
import { noticePlainText } from '@/components/notices/notice-plain-text'
import { SidebarGlyphSlot } from '@/components/sidebar/sidebar-glyph-slot'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useActiveNotice, useDismissNotice } from '@/hooks/use-notices'
import { cn } from '@/lib/utils'

interface SidebarNoticeProps {
  isAuthenticated: boolean
  isCollapsed?: boolean
  className?: string
}

export function SidebarNotice({
  isAuthenticated,
  isCollapsed = false,
  className,
}: SidebarNoticeProps) {
  const { data: notice } = useActiveNotice(isAuthenticated)
  const dismiss = useDismissNotice()

  if (!notice) return null

  const plain = noticePlainText(notice.body)

  if (isCollapsed) {
    return (
      <SidebarMenu className={cn('px-2', className)}>
        <SidebarMenuItem>
          <SidebarMenuButton tooltip={plain} aria-label={plain} tabIndex={-1}>
            <SidebarGlyphSlot>
              <Bell className="size-4 shrink-0" />
            </SidebarGlyphSlot>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <Card
      role="status"
      aria-label={plain}
      className={cn('mx-2 border-primary shadow-none', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <Bell className="size-4 shrink-0 text-primary" aria-hidden />
        {notice.dismissible ? (
          <Button
            type="button"
            variant="ghostToolbar"
            size="iconXs"
            aria-label="Dismiss notice"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate(notice.id)}
          >
            <X />
          </Button>
        ) : null}
      </div>
      <NoticeMarkdown>{notice.body}</NoticeMarkdown>
    </Card>
  )
}
